import { fmtMoney } from "@/lib/format";
import { Info } from "lucide-react";
import { GoodCheck } from "@/components/GoodCheck";

const Row = ({ label, value, muted, strong, testId }) => (
  <div className="flex items-center justify-between border-b border-border/60 py-2.5 last:border-0">
    <span className={`text-sm ${muted ? "text-muted-foreground" : "text-foreground"} ${strong ? "font-600" : ""}`}>
      {label}
    </span>
    <span
      data-testid={testId}
      className={`font-mono-plex text-sm ${strong ? "font-700 text-foreground" : "text-foreground"}`}
    >
      {fmtMoney(value)}
    </span>
  </div>
);

export const PricingTable = ({ result, requestedAddons = [] }) => {
  const pb = result.price_breakdown || {};
  const other = pb.other_fees || [];
  const addonTotal = requestedAddons.reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const preTaxWithAddons = (Number(result.contract_all_in_pre_tax) || 0) + addonTotal;

  // Only replicate the lines that actually appear on the Bill of Sale. If the contract doesn't break out a
  // fee, we don't invent a row for it — so a simple contract shows just price + (requested add-ons) + tax,
  // while an itemized contract shows each fee the dealer listed. (Does NOT affect the price-check math.)
  const has = (v) => v !== null && v !== undefined && v !== "" && !isNaN(Number(v)) && Number(v) > 0;

  return (
    <div data-testid="pricing-breakdown" className="rounded-xl border border-border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-heading text-lg font-800 tracking-tight text-foreground">Submitted Contract Breakdown</h3>
      </div>

      <div className="flex flex-col">
        {has(pb.base_price) && <Row label="Base price" value={pb.base_price} testId="pb-base-price" />}
        {has(pb.admin_fee) && <Row label="Administration fee" value={pb.admin_fee} testId="pb-admin-fee" />}
        {has(pb.safety_certification_fee) && (
          <Row label="Ontario Safety Standards Certificate Fee" value={pb.safety_certification_fee} testId="pb-safety-fee" />
        )}
        {has(pb.regulatory_fee) && <Row label="OMVIC Fee" value={pb.regulatory_fee} testId="pb-omvic-fee" />}
        {other.filter((f) => has(f.amount)).map((f, i) => (
          <Row key={`${f.label || "other-fee"}-${i}`} label={f.label || "Other fee"} value={f.amount} />
        ))}
        {has(pb.discount) && (
          <div className="flex items-center justify-between border-b border-border/60 py-2.5 last:border-0">
            <span className="text-sm text-success">Dealer discount</span>
            <span data-testid="pb-discount" className="font-mono-plex text-sm text-success">−{fmtMoney(pb.discount)}</span>
          </div>
        )}
      </div>

      <div className="mt-4 rounded-lg border border-primary/20 bg-secondary p-4">
        <Row
          label="Contract all-in (pre-tax)"
          value={result.contract_all_in_pre_tax}
          strong
          testId="contract-all-in-value"
        />
        {has(result.hst) && <Row label="HST" value={result.hst} muted />}
        {has(result.licensing_fee) && <Row label="Service Ontario License Fee" value={result.licensing_fee} muted />}
        {(result.financing_fees || []).length > 0
          ? result.financing_fees.map((f, i) => (
              <Row key={`${f.label}-${i}`} label={`${f.label} (financing, after tax)`} value={f.amount} muted />
            ))
          : result.ppsa_lender_fee
          ? <Row label="PPSA / lender fee (financing, after tax)" value={result.ppsa_lender_fee} muted />
          : null}
      </div>

      {requestedAddons.length > 0 && (
        <div
          data-testid="requested-addons"
          className="mt-4 rounded-lg border border-success/40 bg-success-soft p-4"
        >
          <div className="mb-2 flex items-center gap-2">
            <GoodCheck className="h-5 w-5" />
            <span className="font-heading text-sm font-800 uppercase tracking-wide text-success">
              Add-ons you requested
            </span>
          </div>
          <div className="flex flex-col">
            {requestedAddons.map((a, i) => (
              <Row key={`${a.label}-${i}`} label={a.label} value={a.amount} />
            ))}
            <Row
              label="Your pre-tax total (incl. requested add-ons)"
              value={preTaxWithAddons}
              strong
              testId="pre-tax-with-addons"
            />
          </div>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            You confirmed you asked for these, so they're legitimately added to your price — not a hidden
            fee or a compliance concern.
          </p>
        </div>
      )}

      {result.licensing_note && (
        <div
          data-testid="licensing-note"
          className="mt-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft p-3"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <p className="text-xs leading-relaxed text-muted-foreground">{result.licensing_note}</p>
        </div>
      )}

      {result.fees_included && (
        <div
          data-testid="fees-included"
          className="mt-4 rounded-lg border-2 border-success bg-success-soft p-4"
        >
          <div className="flex items-center gap-2">
            <GoodCheck className="h-5 w-5" />
            <span className="font-heading text-sm font-800 uppercase tracking-wide text-success">
              Fees are included in the advertised price
            </span>
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
            Regardless of how the dealer itemized the price, your pre-tax total is at or below the
            advertised price. These fees are included in that price rather than added on top, which is
            compliant.
          </p>
        </div>
      )}
    </div>
  );
};
